/**
 * Tasks ticked but not yet confirmed.
 *
 * The two-stage tick: the first click strikes the row through in place — red,
 * lightened, still exactly where it was — and nothing is sent anywhere. Only
 * the explicit confirmation completes the task on the server; clicking again
 * (or "return") simply clears this set.
 *
 * Deliberately **client-side and ephemeral**. If a pending tick is lost — a
 * refresh, a crash, a closed lid — the task is still OPEN on the server, which
 * is the safe direction to fail: the product's core promise is that an
 * unfinished task can never silently disappear, and an accidental tick that
 * was never confirmed is precisely an unfinished task. The old behaviour
 * (complete immediately, offer an undo toast) failed the other way.
 *
 * A module store rather than component state so the same pending set is seen
 * by every list the task appears in, and survives the row remounting when a
 * query refetches.
 */

let pending = new Set<string>();
const listeners = new Set<() => void>();

function emit(): void {
  // A fresh Set each change: useSyncExternalStore compares by identity.
  pending = new Set(pending);
  for (const listener of listeners) listener();
}

export function markPending(taskId: string): void {
  pending.add(taskId);
  emit();
}

export function clearPending(taskId: string): void {
  if (!pending.delete(taskId)) return;
  emit();
}

export function isPending(taskId: string): boolean {
  return pending.has(taskId);
}

export function getPendingSnapshot(): Set<string> {
  return pending;
}

export function subscribePending(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
